import { useState, useCallback } from "react"

interface ColumnState {
    columnVisibilityModel?: Record<string, boolean>
    columnOrder?: string[]
}

interface UseDataGridColumnPersistenceOptions {
    storageKey: string
    availableColumns: string[]
    defaultVisibility?: Record<string, boolean>
}

interface UseDataGridColumnPersistenceReturn {
    columnVisibilityModel: Record<string, boolean>
    columnOrderState: string[]
    handleColumnVisibilityModelChange: (model: Record<string, boolean>) => void
    handleColumnOrderChange: (params: {
        oldIndex: number
        targetIndex: number
    }) => void
    setColumnOrder: (newOrder: string[]) => void
    resetColumnState: () => void
}

/**
 * Custom hook for persisting DataGrid column visibility and order in localStorage
 * with automatic validation and cleanup of invalid columns
 */
export const useDataGridColumnPersistence = ({
    storageKey,
    availableColumns,
    defaultVisibility = {},
}: UseDataGridColumnPersistenceOptions): UseDataGridColumnPersistenceReturn => {
    // Load column state from localStorage with validation
    const getStoredColumnState = useCallback((): ColumnState | null => {
        try {
            const stored = localStorage.getItem(storageKey)
            if (!stored) return null

            const parsed = JSON.parse(stored) as ColumnState

            // Validate and clean visibility model
            if (parsed.columnVisibilityModel) {
                const cleanedVisibility: Record<string, boolean> = {}
                Object.entries(parsed.columnVisibilityModel).forEach(
                    ([key, value]) => {
                        if (
                            availableColumns.includes(key) &&
                            typeof value === "boolean"
                        ) {
                            cleanedVisibility[key] = value
                        }
                    }
                )
                parsed.columnVisibilityModel = cleanedVisibility
            }

            // Validate and clean column order
            if (parsed.columnOrder) {
                const cleanedOrder = parsed.columnOrder.filter((column) =>
                    availableColumns.includes(column)
                )
                parsed.columnOrder = cleanedOrder
            }

            return parsed
        } catch {
            return null
        }
    }, [storageKey, availableColumns])

    // Save column state to localStorage
    const saveColumnState = useCallback(
        (
            visibilityModel: Record<string, boolean>,
            columnOrder: string[]
        ): void => {
            try {
                // Only save valid columns
                const validVisibility: Record<string, boolean> = {}
                Object.entries(visibilityModel).forEach(([key, value]) => {
                    if (availableColumns.includes(key)) {
                        validVisibility[key] = value
                    }
                })

                const validOrder = columnOrder.filter((column) =>
                    availableColumns.includes(column)
                )

                localStorage.setItem(
                    storageKey,
                    JSON.stringify({
                        columnVisibilityModel: validVisibility,
                        columnOrder: validOrder,
                    })
                )
            } catch {
                // Handle localStorage errors silently
            }
        },
        [storageKey, availableColumns]
    )

    // Initialize state from localStorage or defaults
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        const stored = getStoredColumnState()
        return {
            ...defaultVisibility,
            ...stored?.columnVisibilityModel,
        }
    })

    const [columnOrderState, setColumnOrderState] = useState(() => {
        const stored = getStoredColumnState()
        return stored?.columnOrder || []
    })

    // Handle column visibility changes
    const handleColumnVisibilityModelChange = useCallback(
        (model: Record<string, boolean>): void => {
            setColumnVisibilityModel(model)
            saveColumnState(model, columnOrderState)
        },
        [saveColumnState, columnOrderState]
    )

    // Handle column order changes
    const handleColumnOrderChange = useCallback(
        (params: { oldIndex: number; targetIndex: number }): void => {
            if (params.oldIndex !== params.targetIndex) {
                // Create new order array based on the move
                const newOrder = [...availableColumns]
                const [movedItem] = newOrder.splice(params.oldIndex, 1)
                newOrder.splice(params.targetIndex, 0, movedItem)

                setColumnOrderState(newOrder)
                saveColumnState(columnVisibilityModel, newOrder)
            }
        },
        [availableColumns, columnVisibilityModel, saveColumnState]
    )

    // Set column order directly (for custom reorder interfaces)
    const setColumnOrder = useCallback(
        (newOrder: string[]): void => {
            // Validate that all columns in newOrder are available
            const validOrder = newOrder.filter((column) =>
                availableColumns.includes(column)
            )

            setColumnOrderState(validOrder)
            saveColumnState(columnVisibilityModel, validOrder)
        },
        [availableColumns, columnVisibilityModel, saveColumnState]
    )

    // Reset to defaults
    const resetColumnState = useCallback((): void => {
        setColumnVisibilityModel(defaultVisibility)
        setColumnOrderState([])
        try {
            localStorage.removeItem(storageKey)
        } catch {
            // Handle localStorage errors silently
        }
    }, [defaultVisibility, storageKey])

    return {
        columnVisibilityModel,
        columnOrderState,
        handleColumnVisibilityModelChange,
        handleColumnOrderChange,
        setColumnOrder,
        resetColumnState,
    }
}
